import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { StatsSection } from "@/components/admin/dashboard/StatsSection";
import { RecentActivities } from "@/components/admin/dashboard/RecentActivities";
import { DashboardLayout } from "@/components/layout/DashboardLayout";

interface DashboardStats {
  companies: number;
  users: number;
  businessTypes: number;
}

const Dashboard = () => {
  const { data: stats } = useQuery<DashboardStats>({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      const [companies, users, businessTypes] = await Promise.all([
        supabase.from('companies').select('*', { count: 'exact', head: true }),
        supabase.from('profiles').select('*', { count: 'exact', head: true }),
        supabase.from('business_types').select('*', { count: 'exact', head: true })
      ]);

      return {
        companies: companies.count || 0,
        users: users.count || 0,
        businessTypes: businessTypes.count || 0
      };
    }
  });

  const { data: recentActivities } = useQuery({
    queryKey: ['recent-activities'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('poultry_transactions')
        .select(`
          id,
          transaction_type,
          total_amount,
          created_at,
          created_by,
          stock_items (
            business_stocks (
              business_id
            )
          )
        `)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) {
        console.error('Error fetching activities:', error);
        return [];
      }

      // Fetch user names separately
      const userIds = data?.map(t => t.created_by).filter(Boolean) || [];
      const { data: users, error: usersError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', userIds);

      if (usersError) {
        console.error('Error fetching users:', usersError);
        return [];
      }

      const usersMap = new Map(users?.map(u => [u.id, u]) || []);

      return data.map(transaction => ({
        id: transaction.id,
        type: transaction.transaction_type,
        amount: transaction.total_amount,
        date: transaction.created_at,
        user: {
          full_name: transaction.created_by ? usersMap.get(transaction.created_by)?.full_name || 'N/A' : 'N/A'
        }
      }));
    },
  });

  return (
    <DashboardLayout>
      <div className="container mx-auto p-6 space-y-6">
        <h1 className="text-2xl font-bold">Tableau de bord administrateur</h1>
        {stats && <StatsSection dashboardStats={stats} />}
        <RecentActivities activities={recentActivities || []} />
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;